import * as AgGrid from "ag-grid-community";
import * as datasource from "grid/datasource";
import * as gridUtils from "grid/utils";
import * as rowActions from "./row_actions";
import { Action } from "./row_actions";
import { ToggleAllRows } from "grid/toggleAllRows";
import { UuidTextFilter } from "grid/uuid_text_filter";
import {
  AMOUNT_COL_DEF,
  DEFAULT_COL_DEF,
  ACTIONS_COL_DEF,
  TEXT_FILTER_COL_DEF,
  AMOUNT_FILTER_COL_DEF,
  DATE_FILTER_COL_DEF,
} from "../../grid/columns";
import { BulkAction } from "grid/bulk_action";
import { GridController } from "grid/grid_controller";
import { Payment } from "../../types/payment";
import { StatusCheckboxFilter } from "grid/status_checkbox_filter";
import { HasRefundRequestsFilter } from "grid/has_refund_requests_filter";
import { IsRefundableFilter } from "grid/is_refundable_filter";

class SelectionManager {
  private customerNumber: String | null = null;
  onRowSelected(params: AgGrid.RowSelectedEvent<Payment, any>) {
    this.preventMultipleCustomerNumberSelection(params);
  }

  private preventMultipleCustomerNumberSelection(params: AgGrid.RowSelectedEvent<Payment, any>) {
    this.customerNumber ||= params.data.customer_number;

    if (this.customerNumber != params.data.customer_number) {
      params.node.setSelected(false);
    }

    if (params.api.getSelectedRows().length == 0) {
      this.customerNumber = null;
    }
  }
}

interface RefundRequestModalData {
  node: AgGrid.IRowNode<Payment>;
  gridApi: AgGrid.GridApi<Payment>;
  originalActions: string;
}

const refundRequestModalDataStore = new Map<string, RefundRequestModalData>();

function openRefundRequestModal(
  params: AgGrid.CellClickedEvent<Payment>,
  node: AgGrid.IRowNode<Payment>,
  gridApi: AgGrid.GridApi<Payment>,
): void {
  const maxAmount = params.data.amount_captured || params.data.amount;
  const paymentId = params.data.payment_id;
  const paymentStatus = params.data.status;

  const originalActions = node.data.actions || "";

  refundRequestModalDataStore.set(paymentId, {
    node,
    gridApi,
    originalActions,
  });

  const url = `/payments/refund_requests/new_from_payment?payment_id=${encodeURIComponent(paymentId)}&payment_status=${encodeURIComponent(paymentStatus)}&max_amount=${encodeURIComponent(maxAmount)}`;

  const turboFrame = document.querySelector("turbo-frame#modal") as HTMLElement & { src?: string };
  if (turboFrame) {
    turboFrame.src = url;
  }
}

export function getRefundRequestModalData(paymentId: string): RefundRequestModalData | undefined {
  return refundRequestModalDataStore.get(paymentId);
}

export function removeRefundRequestModalData(paymentId: string): void {
  refundRequestModalDataStore.delete(paymentId);
}

export default class extends GridController<Payment> {
  static targets = ["grid", "searchInput"];
  static values = Object.assign(super.values, {
    bulkPaymentUrl: String,
    feesUrl: String,
    isSelectionEnabled: Boolean,
  });

  declare readonly gridTarget: HTMLInputElement;
  declare readonly hasGridTarget: boolean;
  declare readonly searchInputTarget: HTMLInputElement;
  declare readonly hasSearchInputTarget: boolean;
  declare readonly totalPaymentAmountTarget: HTMLInputElement;
  declare readonly isSelectionEnabledValue: boolean;

  bulkPayAction?: BulkAction<Payment>;

  private appliedSearch = "";

  connect() {
    super.connect();
  }

  applySearch(): void {
    this.appliedSearch = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : "";
    this.refreshSearch();
  }

  private refreshSearch(): void {
    if (!this.gridApi) return;
    this.gridApi.paginationGoToPage(0);
    this.gridApi.purgeInfiniteCache();
  }

  override buildGrid() {
    const ctrl = this;
    const { i18nJson, paginationPageSize } = this.gridTarget.dataset;
    const selectionManager = new SelectionManager();
    const grantsStr = this.gridTarget.dataset.userGrants || this.gridTarget.dataset.userGrant || "";
    const grants = grantsStr
      .split(",")
      .map((g) => g.trim())
      .filter(Boolean);
    const isGroupOperator = grants.includes("GROUP_OPERATOR");
    const isPlatformOperator = grants.includes("PLATFORM_OPERATOR");
    const showSellerId = isPlatformOperator || isGroupOperator;
    const i18n = JSON.parse(i18nJson);

    const paymentStatusValues = ["ORDER_PLACED", "ORDER_CONFIRMED", "ORDER_CANCELED", "ORDER_PAID_OUT"];
    const paymentStatusLabels = Object.fromEntries(
      paymentStatusValues.map((code) => [code, i18n[`status_${code.toLowerCase()}`] || code]),
    );
    const colDefs: AgGrid.ColDef[] = [
      {
        field: "payment_id",
        ...DEFAULT_COL_DEF,
        filter: "uuidTextFilter",
        sortable: false,
        filterParams: {
          locales: {
            ...(i18n?.grid?.dropdown_filter || {}),
            ...(i18n?.grid?.uuid_text_filter || {}),
          },
        },
      },
      ...(showSellerId
        ? [
            {
              field: "seller_id",
              colId: "dtpay_seller_id",
              ...DEFAULT_COL_DEF,
              filter: "uuidTextFilter",
              sortable: false,
              filterParams: {
                locales: {
                  ...(i18n?.grid?.dropdown_filter || {}),
                  ...(i18n?.grid?.uuid_text_filter || {}),
                },
              },
            },
          ]
        : []),
      { field: "amount", colId: "auth_amount", ...AMOUNT_COL_DEF, ...AMOUNT_FILTER_COL_DEF, sortable: true },
      {
        field: "amount_captured",
        colId: "capture_amount",
        ...AMOUNT_COL_DEF,
        ...AMOUNT_FILTER_COL_DEF,
        sortable: true,
      },
      {
        field: "status",
        ...DEFAULT_COL_DEF,
        filter: "statusCheckboxFilter",
        filterParams: {
          values: paymentStatusValues,
          labels: paymentStatusLabels,
          locales: i18n?.grid?.dropdown_filter || {},
        },
        valueFormatter: (p) => paymentStatusLabels[p.value] || p.value,
        sortable: true,
      },
      { field: "order_id", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: true },
      { field: "currency_code", ...DEFAULT_COL_DEF, sortable: false },
      {
        field: "payment_created_at",
        colId: "created_at",
        ...DEFAULT_COL_DEF,
        ...DATE_FILTER_COL_DEF,
        sortable: true,
        sort: "desc",
      },
      {
        field: "is_refundable",
        ...DEFAULT_COL_DEF,
        hide: true,
        sortable: false,
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["reset", "apply"],
          closeOnApply: true,
          filterOptions: ["equals"],
        },
      },
      {
        field: "has_refund_requests",
        headerName: i18n["refund_request"] || "Refund Requests",
        ...DEFAULT_COL_DEF,
        enableCellChangeFlash: true,
        sortable: false,
        filter: "hasRefundRequestsFilter",
        filterParams: {
          labelAll: i18n["has_refund_requests_all"] || "All",
          labelWithout: i18n["has_refund_requests_without"] || "Without refund requests",
          labelWith: i18n["has_refund_requests_with"] || "With refund requests",
        },
        valueGetter: (p) => p.data?.refund_request,
      },
      ...(isGroupOperator
        ? []
        : [
            {
              ...ACTIONS_COL_DEF,
              cellClass: "actions-cell",
              filter: "isRefundableFilter",
              filterParams: {
                labelAll: i18n["is_refundable_all"] || "All",
                labelRefundable: i18n["is_refundable_true"] || "Refundable",
                labelNotRefundable: i18n["is_refundable_false"] || "Not refundable",
              },
              onCellClicked: (params) => {
                rowActions.onActionCellClicked(params, async function (action) {
                  if (Action.REQUEST_REFUND === action) {
                    gridApi.forEachNode((node) => {
                      if (node.data.id == params.data.id) {
                        openRefundRequestModal(params, node, gridApi);
                      }
                    });
                  }
                });
              },
            },
          ]),
    ];

    const dataSource = new datasource.Datasource({
      callbacks: {
        getSearchInput: () => ctrl.appliedSearch.trim() || undefined,
      },
      limit: paginationPageSize,
    });
    // prevent double-acting with checkbox
    const { maxBlocksInCache, onCellFocused, ...defaultOptions } = gridUtils.DEFAULT_GRID_OPTIONS;

    const options: AgGrid.GridOptions<Payment> = {
      ...defaultOptions,
      components: {
        ...(defaultOptions.components || {}),
        statusCheckboxFilter: StatusCheckboxFilter,
        hasRefundRequestsFilter: HasRefundRequestsFilter,
        isRefundableFilter: IsRefundableFilter,
        uuidTextFilter: UuidTextFilter,
      },
      defaultColDef: {
        ...(gridUtils.DEFAULT_GRID_OPTIONS.defaultColDef as AgGrid.ColDef<Payment, any>),
      },
      datasource: dataSource,
      onCellEditingStopped(params) {
        if (params.newValue <= 0 && params.column.getColId() === "payment_amount") {
          params.node.setDataValue(params.column, params.oldValue);
        }
      },
      editType: "fullRow",
      cacheBlockSize: parseInt(paginationPageSize),
      columnDefs: gridUtils.buildColumnDefs(colDefs, { i18n }),
      pagination: true,
      paginationPageSize: parseInt(paginationPageSize),
    };

    const selectionOptions: AgGrid.GridOptions<Payment> = ctrl.isSelectionEnabledValue
      ? {
          rowSelection: "multiple",
          suppressRowClickSelection: true,
          suppressRowDeselection: true,
          onRowSelected(event) {
            ctrl.bulkPayAction.onRowSelected(event);
            selectionManager.onRowSelected(event);
          },
        }
      : {};

    const gridApi = AgGrid.createGrid(this.gridTarget, { ...options, ...selectionOptions });
    dataSource.gridApi = gridApi;
    return gridApi;
  }
}
